// TRON 链上数据：直接调用 TronGrid 公共接口（支持跨域、无需密钥）
export const TRONGRID = 'https://api.trongrid.io'
export const USDT_TRC20 = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'
export const TRON_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const BASE58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

async function sha256(bytes) {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
}

function base58Decode(str) {
  let bytes = [0]
  for (const ch of str) {
    const v = BASE58.indexOf(ch)
    if (v < 0) throw new Error('bad base58')
    let carry = v
    for (let i = 0; i < bytes.length; i++) {
      carry += bytes[i] * 58
      bytes[i] = carry & 0xff
      carry >>= 8
    }
    while (carry) { bytes.push(carry & 0xff); carry >>= 8 }
  }
  for (const ch of str) { if (ch === '1') bytes.push(0); else break }
  return new Uint8Array(bytes.reverse())
}

function base58Encode(bytes) {
  const digits = [0]
  for (const byte of bytes) {
    let carry = byte
    for (let i = 0; i < digits.length; i++) {
      carry += digits[i] << 8
      digits[i] = carry % 58
      carry = (carry / 58) | 0
    }
    while (carry) { digits.push(carry % 58); carry = (carry / 58) | 0 }
  }
  let out = ''
  for (const b of bytes) { if (b === 0) out += '1'; else break }
  for (let i = digits.length - 1; i >= 0; i--) out += BASE58[digits[i]]
  return out
}

// T 地址 -> 20 字节 hex（校验长度、前缀和双 sha256 校验和）
export async function tronAddressToHex(addr) {
  const raw = base58Decode(addr)
  if (raw.length !== 25 || raw[0] !== 0x41) throw new Error('bad tron address')
  const payload = raw.slice(0, 21)
  const check = await sha256(await sha256(payload))
  for (let i = 0; i < 4; i++) if (check[i] !== raw[21 + i]) throw new Error('bad checksum')
  return Array.from(payload.slice(1)).map(b => b.toString(16).padStart(2, '0')).join('')
}

// 0x 开头的 20 字节 hex -> T 地址
export async function hexToTronAddress(hex) {
  const clean = hex.replace(/^0x/, '').replace(/^41/, '').padStart(40, '0')
  const payload = new Uint8Array(21)
  payload[0] = 0x41
  for (let i = 0; i < 20; i++) payload[i + 1] = parseInt(clean.substr(i * 2, 2), 16)
  const check = await sha256(await sha256(payload))
  const full = new Uint8Array(25)
  full.set(payload, 0)
  full.set(check.slice(0, 4), 21)
  return base58Encode(full)
}

export function formatAmount(value, decimals) {
  const n = Number(value || 0) / Math.pow(10, decimals)
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

async function getJSON(url, options = {}) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), 15000)
  try {
    const res = await fetch(url, { ...options, signal: ctrl.signal })
    if (!res.ok) throw new Error('HTTP ' + res.status)
    return await res.json()
  } finally {
    clearTimeout(timer)
  }
}

// 首页冻结地址列表 + 24 小时数量：走自己的 /api/feed（服务端缓存，所有访客共用），
// 不再由每个浏览器直接请求 TronGrid，避免连续刷新被限流
const FEED_LS_KEY = 'musesec-feed-cache'
let feedPromise = null
let feedAt = 0

function readFeedCache() {
  try { return JSON.parse(localStorage.getItem(FEED_LS_KEY) || 'null') } catch (e) { return null }
}

async function loadFeed() {
  const data = await getJSON('/api/feed')
  if (!Array.isArray(data.items) || !data.items.length) throw new Error('empty')
  try { localStorage.setItem(FEED_LS_KEY, JSON.stringify(data)) } catch (e) { /* ignore */ }
  return data
}

// 同一页面里多个组件共用一次请求；30 秒内重复调用直接复用
export function fetchFeed(force = false) {
  if (!force && feedPromise && Date.now() - feedAt < 30000) return feedPromise
  feedAt = Date.now()
  feedPromise = loadFeed().catch(err => {
    feedPromise = null
    // 服务端也拿不到时，用这个浏览器上次成功的数据兜底（最多 24 小时内）
    const last = readFeedCache()
    if (last && Date.now() - last.updatedAt < 24 * 3600 * 1000) return { ...last, offline: true }
    throw err
  })
  return feedPromise
}

// 黑U检测：USDT 合约 isBlackListed + 账户余额
export async function checkAddress(addr) {
  const hex = await tronAddressToHex(addr)
  const [black, account] = await Promise.all([
    getJSON(`${TRONGRID}/wallet/triggerconstantcontract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        owner_address: addr,
        contract_address: USDT_TRC20,
        function_selector: 'isBlackListed(address)',
        parameter: hex.padStart(64, '0'),
        visible: true,
      }),
    }),
    getJSON(`${TRONGRID}/v1/accounts/${addr}`),
  ])
  const flag = black?.constant_result?.[0]
  if (!black?.result?.result || typeof flag !== 'string') throw new Error('blacklist query failed')
  const acc = account?.data?.[0] || {}
  const trc20 = Array.isArray(acc.trc20) ? acc.trc20 : []
  const usdt = trc20.find(t => Object.prototype.hasOwnProperty.call(t, USDT_TRC20))
  return {
    address: addr,
    isRisk: /^0*1$/.test(flag),
    trx: formatAmount(acc.balance, 6),
    usdt: formatAmount(usdt ? usdt[USDT_TRC20] : 0, 6),
    tokens: trc20.length,
  }
}
